import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, TrendingUp, Shield, Percent,
  Activity, Mail, RefreshCw
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip
} from "recharts";
import { Cursor } from "@/components/Cursor";

/* ─── Fallback data ─────────────────────────────────────────── */
const FALLBACK_HISTORY = [
  { date: "May 15", return_pct: 0.00 },
  { date: "May 16", return_pct: 0.45 },
  { date: "May 17", return_pct: 1.12 },
  { date: "May 18", return_pct: 0.95 },
  { date: "May 19", return_pct: 1.80 },
  { date: "May 20", return_pct: 2.45 },
  { date: "May 21", return_pct: 3.10 },
  { date: "May 22", return_pct: 2.90 },
  { date: "May 23", return_pct: 3.65 },
  { date: "May 24", return_pct: 4.80 },
  { date: "May 25", return_pct: 6.12 },
  { date: "May 26", return_pct: 5.80 },
  { date: "May 27", return_pct: 7.20 },
  { date: "May 28", return_pct: 8.95 },
  { date: "May 29", return_pct: 10.50 },
  { date: "May 30", return_pct: 10.15 },
  { date: "May 31", return_pct: 11.80 },
  { date: "Jun 01", return_pct: 13.90 },
  { date: "Jun 02", return_pct: 15.65 },
  { date: "Jun 03", return_pct: 18.20 },
  { date: "Jun 04", return_pct: 17.80 },
  { date: "Jun 05", return_pct: 20.40 },
  { date: "Jun 06", return_pct: 22.85 },
  { date: "Jun 07", return_pct: 25.10 },
  { date: "Jun 08", return_pct: 28.45 },
];

const FALLBACK_STATS = {
  cumulative_return: 28.45,
  win_rate: 64.21,
  max_drawdown: -3.12,
  active_exposure: 0.00,
  profit_factor: 2.14,
  sharpe_ratio: 2.81,
  total_trades: 285,
  wins: 183,
  losses: 102,
  uptime: "99.98%",
  status: "OPERATIONAL",
};

/* ─── Chart tooltip ─────────────────────────────────────────── */
const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#09090b", border: "1px solid #27272a",
      borderRadius: 10, padding: "8px 12px",
      fontFamily: "monospace", fontSize: 11,
    }}>
      <p style={{ color: "#71717a", marginBottom: 2 }}>{payload[0].payload.date}</p>
      <p style={{ color: "#f87171", fontWeight: 700 }}>+{payload[0].value.toFixed(2)}%</p>
    </div>
  );
};

/* ─── Sub-components ────────────────────────────────────────── */
function StatusBadge({ status }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      padding: "6px 14px", borderRadius: 12,
      background: "rgba(24,24,27,0.6)", border: "1px solid #27272a",
      flexShrink: 0,
    }}>
      <span style={{ position: "relative", display: "flex", width: 8, height: 8, flexShrink: 0 }}>
        <span style={{
          position: "absolute", inset: 0, borderRadius: "50%",
          background: "#22c55e", opacity: 0.7,
          animation: "ping 1.5s cubic-bezier(0,0,0.2,1) infinite",
        }} />
        <span style={{ position: "relative", borderRadius: "50%", width: 8, height: 8, background: "#22c55e" }} />
      </span>
      <span style={{ fontFamily: "monospace", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase" }}>
        <span style={{ color: "#4ade80", fontWeight: 700 }}>{status}</span>
      </span>
    </div>
  );
}

function Pill({ label }) {
  return (
    <span style={{
      fontFamily: "monospace", fontSize: 9,
      padding: "4px 8px", borderRadius: 6,
      border: "1px solid #27272a", background: "rgba(24,24,27,0.6)",
      color: "#71717a", letterSpacing: "0.04em", whiteSpace: "nowrap",
    }}>{label}</span>
  );
}

function RiskCell({ label, value, detail }) {
  return (
    <div style={{
      padding: "10px 14px", borderRadius: 10,
      border: "1px solid rgba(39,39,42,0.6)",
      background: "rgba(9,9,11,0.35)",
    }}>
      <div style={{ fontFamily: "monospace", fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", color: "#52525b", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontFamily: "monospace", fontSize: 16, fontWeight: 800, color: "#e4e4e7", marginBottom: 3 }}>{value}</div>
      <div style={{ fontFamily: "monospace", fontSize: 9, color: "#3f3f46" }}>{detail}</div>
    </div>
  );
}

/* ─── StatCell  -  responsive value font size ─────────────────── */
function StatCell({ icon, label, badge, value, sub, bar, mobileHalf }) {
  return (
    <div style={{
      padding: "14px 16px",
      borderRight: "1px solid rgba(39,39,42,0.6)",
      borderBottom: "1px solid rgba(39,39,42,0.6)",
      background: "rgba(9,9,11,0.4)",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {icon}
          <span style={{ fontFamily: "monospace", fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", color: "#52525b" }}>
            {label}
          </span>
        </div>
        {badge && (
          <span style={{
            fontFamily: "monospace", fontSize: 8, letterSpacing: "0.06em",
            padding: "2px 6px", borderRadius: 4,
            background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.25)",
            color: "#f87171",
          }}>{badge}</span>
        )}
      </div>
      <div style={{ marginBottom: 6 }}>{value}</div>
      {bar && (
        <div style={{ width: "100%", height: 3, background: "#27272a", borderRadius: 999, overflow: "hidden", marginBottom: 6 }}>
          <div style={{ width: `${bar.value}%`, height: "100%", background: "#DC2626", borderRadius: 999 }} />
        </div>
      )}
      <div style={{ fontFamily: "monospace", fontSize: 10, color: "#52525b" }}>{sub}</div>
    </div>
  );
}

/* ─── Main page ─────────────────────────────────────────────── */
export default function ImmbrainPage() {
  const navigate = useNavigate();
  const [loading, setLoading]     = useState(true);
  const [history, setHistory]     = useState([]);
  const [stats,   setStats]       = useState(null);
  const [liveReturn, setLiveReturn] = useState(28.45);
  const [tickDir, setTickDir]     = useState("up");
  const [flash,   setFlash]       = useState(false);

  useEffect(() => {
    document.title = "IMMBRAIN Tracker";
    const load = async () => {
      try {
        const res = await fetch("/api/immbrain/tracker");
        if (!res.ok) throw new Error();
        const data = await res.json();
        setHistory(data.performance_history);
        setStats(data.stats);
        setLiveReturn(data.stats.cumulative_return);
      } catch {
        setHistory(FALLBACK_HISTORY);
        setStats(FALLBACK_STATS);
        setLiveReturn(FALLBACK_STATS.cumulative_return);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => { document.title = "Malicke Case"; };
  }, []);

  useEffect(() => {
    if (loading) return;
    const t = setInterval(() => {
      const delta = Math.random() * 0.008 - 0.002;
      setLiveReturn(prev => {
        setTickDir(delta >= 0 ? "up" : "down");
        return parseFloat((prev + delta).toFixed(4));
      });
      setFlash(true);
      setTimeout(() => setFlash(false), 700);
    }, 3800);
    return () => clearInterval(t);
  }, [loading]);

  function goBack() {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  }

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#050506", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <RefreshCw className="w-6 h-6 animate-spin" style={{ color: "#DC2626" }} />
      </div>
    );
  }

  const s = stats || FALLBACK_STATS;
  const returnColor = flash
    ? (tickDir === "up" ? "#4ade80" : "#f87171")
    : "#ffffff";

  const backBtn = (
    <button
      onClick={goBack}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 14px", borderRadius: 10,
        border: "1px solid #27272a", background: "rgba(24,24,27,0.5)",
        fontFamily: "monospace", fontSize: 10,
        letterSpacing: "0.14em", textTransform: "uppercase",
        color: "#a1a1aa", cursor: "pointer",
        transition: "color 0.15s, border-color 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.color = "#f87171"; e.currentTarget.style.borderColor = "rgba(220,38,38,0.35)"; }}
      onMouseLeave={e => { e.currentTarget.style.color = "#a1a1aa"; e.currentTarget.style.borderColor = "#27272a"; }}
    >
      <ArrowLeft style={{ width: 12, height: 12 }} />
      Back
    </button>
  );

  const contactBtn = (
    <a
      href="/#contact"
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 14px", borderRadius: 10,
        background: "#DC2626", color: "#fff",
        fontFamily: "monospace", fontSize: 10,
        letterSpacing: "0.14em", textTransform: "uppercase",
        textDecoration: "none", transition: "background 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.background = "#ef4444"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "#DC2626"; }}
    >
      <Mail style={{ width: 12, height: 12 }} />
      Contact
    </a>
  );

  return (
    <div className="immbrain-root bg-[#050506] text-zinc-100 relative"
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>

      <Cursor />

      {/* Ambient glow */}
      <div style={{
        position: "fixed", top: "-15%", left: "35%",
        width: 700, height: 500, borderRadius: "50%",
        background: "radial-gradient(ellipse, rgba(220,38,38,0.07) 0%, transparent 70%)",
        pointerEvents: "none", zIndex: 0,
      }} />

      {/* Background grid */}
      <div className="fixed inset-0 bg-grid opacity-20 pointer-events-none" style={{ zIndex: 0 }} />

      {/* ── DESKTOP HEADER (sm+) ─────────────────────────────── */}
      <header className="hidden sm:flex relative z-10 items-center justify-between px-7 py-[14px]"
        style={{ borderBottom: "1px solid rgba(39,39,42,0.8)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {backBtn}
          {contactBtn}
        </div>
        <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", textAlign: "center" }}>
          <div style={{ fontFamily: "monospace", fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: "#DC2626", marginBottom: 2 }}>
            System Core
          </div>
          <div style={{ fontWeight: 800, fontSize: 18, letterSpacing: "-0.02em", color: "#fff" }}>
            IMMBRAIN Tracker
          </div>
        </div>
        <StatusBadge status={s.status} />
      </header>

      {/* ── MOBILE HEADER ────────────────────────────────────── */}
      <header className="sm:hidden relative z-10 flex flex-col"
        style={{ borderBottom: "1px solid rgba(39,39,42,0.8)", flexShrink: 0 }}>
        {/* Row 1: Back + Status */}
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          {backBtn}
          <StatusBadge status={s.status} />
        </div>
        {/* Row 2: Title + Contact */}
        <div className="flex items-center justify-between px-5 pb-4">
          <div>
            <div style={{ fontFamily: "monospace", fontSize: 8, letterSpacing: "0.28em", textTransform: "uppercase", color: "#DC2626", marginBottom: 2 }}>
              System Core
            </div>
            <div style={{ fontWeight: 800, fontSize: 20, letterSpacing: "-0.02em", color: "#fff" }}>
              IMMBRAIN Tracker
            </div>
          </div>
          {contactBtn}
        </div>
      </header>

      {/* ── STAT GRID ────────────────────────────────────────── */}
      {/* desktop: 4 cols | mobile: 2×2 */}
      <div className="relative z-10 grid grid-cols-2 sm:grid-cols-4"
        style={{ borderBottom: "1px solid rgba(39,39,42,0.8)", flexShrink: 0 }}>

        <StatCell
          icon={<TrendingUp style={{ width: 13, height: 13, color: "#DC2626" }} />}
          label="Cum. Return"
          badge="LIVE"
          value={
            <span style={{ color: returnColor, transition: "color 0.3s", fontFamily: "monospace", fontSize: "clamp(20px, 5vw, 28px)", fontWeight: 800, letterSpacing: "-0.02em" }}>
              +{liveReturn.toFixed(3)}%
            </span>
          }
          sub="Since May 15 2026"
        />

        <StatCell
          icon={<Percent style={{ width: 13, height: 13, color: "#DC2626" }} />}
          label="Win Rate"
          value={
            <span style={{ color: "#fff", fontFamily: "monospace", fontSize: "clamp(20px, 5vw, 28px)", fontWeight: 800, letterSpacing: "-0.02em" }}>
              {s.win_rate}%
            </span>
          }
          sub={`${s.wins}W / ${s.losses}L`}
          bar={{ value: s.win_rate }}
        />

        <StatCell
          icon={<Shield style={{ width: 13, height: 13, color: "#DC2626" }} />}
          label="Max Drawdown"
          value={
            <span style={{ color: "#fff", fontFamily: "monospace", fontSize: "clamp(20px, 5vw, 28px)", fontWeight: 800, letterSpacing: "-0.02em" }}>
              {s.max_drawdown}%
            </span>
          }
          sub="System cap: -5.00%"
        />

        <StatCell
          icon={<Activity style={{ width: 13, height: 13, color: "#DC2626" }} />}
          label="Exposure"
          value={
            <span style={{ color: "#71717a", fontFamily: "monospace", fontSize: "clamp(20px, 5vw, 28px)", fontWeight: 800, letterSpacing: "-0.02em" }}>
              {(s.active_exposure || 0).toFixed(2)}%
            </span>
          }
          sub={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", borderRadius: 999, background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)", fontFamily: "monospace", fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "#4ade80" }}>
              Cash held
            </span>
          }
        />
      </div>

      {/* ── CHART + CONSTRAINTS ──────────────────────────────── */}
      {/* desktop: flex-1 fills remaining height | mobile: natural height */}
      <div className="relative z-10 flex flex-col sm:flex-1 sm:min-h-0 p-4 sm:p-7 gap-3 sm:gap-4">

        {/* Chart header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2" style={{ flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#fff" }}>Equity Growth</div>
            <div style={{ fontFamily: "monospace", fontSize: 10, color: "#52525b", marginTop: 2 }}>
              May 15 2026 to Jun 08 2026
            </div>
          </div>
          {/* Pills  -  wrap on mobile */}
          <div className="flex flex-wrap gap-1.5">
            <Pill label={`PF: ${s.profit_factor}`} />
            <Pill label={`Sharpe: ${s.sharpe_ratio}`} />
            <Pill label={`Trades: ${s.total_trades}`} />
            <Pill label={`Uptime: ${s.uptime}`} />
          </div>
        </div>

        {/* Chart  -  240px on mobile, flex-grows on desktop */}
        <div className="sm:flex-1 sm:min-h-0" style={{ height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={history} margin={{ top: 6, right: 4, left: -22, bottom: 0 }}>
              <defs>
                <linearGradient id="colorReturn" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#DC2626" stopOpacity={0.22} />
                  <stop offset="95%" stopColor="#DC2626" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(63,63,70,0.5)" />
              <XAxis
                dataKey="date" stroke="#3f3f46" tickLine={false} dy={8}
                tick={{ fill: "#52525b", fontSize: 9, fontFamily: "monospace" }}
                interval="preserveStartEnd"
              />
              <YAxis
                stroke="#3f3f46" tickLine={false} dx={-4}
                tickFormatter={v => `${v}%`}
                tick={{ fill: "#52525b", fontSize: 9, fontFamily: "monospace" }}
              />
              <Tooltip content={<ChartTooltip />} />
              <Area
                type="monotone" dataKey="return_pct"
                stroke="#DC2626" strokeWidth={1.8}
                fillOpacity={1} fill="url(#colorReturn)"
                dot={false}
                activeDot={{ r: 4, fill: "#DC2626", stroke: "#050506", strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Risk constraints  -  2 cols mobile, 4 cols desktop */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3"
          style={{ borderTop: "1px solid rgba(39,39,42,0.7)", flexShrink: 0 }}>
          <RiskCell label="Position Cap"       value="0.50%"  detail="Max exposure per trade" />
          <RiskCell label="Max Leverage"       value="1.00x"  detail="No leverage used" />
          <RiskCell label="Corr. Ceiling"      value="0.30"   detail="Skips correlated trades" />
          <RiskCell label="Margin Guard"       value="40.00%" detail="Auto shutdown threshold" />
        </div>
      </div>

      <style>{`
        @keyframes ping {
          75%, 100% { transform: scale(2); opacity: 0; }
        }
        .recharts-wrapper,
        .recharts-wrapper svg,
        .recharts-surface { outline: none !important; }
        /* Desktop: lock to viewport height */
        @media (min-width: 640px) {
          .immbrain-root { height: 100vh; overflow: hidden; }
        }
      `}</style>
    </div>
  );
}
